// 11 §11.1 RouteStore. Only row-change handlers (ui/RouteDriver.tsx) or user action call `go`;
// scenes never route themselves. `encounterId` outlives the party's `encounter_id` so the
// Aftermath screen can show results after the server has cleared the party row.
// docs/141: some moves go through the black (`screenCurtain.curtainFor`): the screen changes once it is down.
import { create } from 'zustand';
import { cover, curtainFor, curtainPending, uncover } from './screenCurtain';

export type Screen = 'title' | 'online' | 'gathering' | 'writing' | 'intro' | 'epilogue' | 'chapter' | 'world' | 'lobby' | 'localmap' | 'encounter' | 'spoils' | 'aftermath' | 'roster' | 'levelup' | 'inventory' | 'ledger' | 'realm' | 'settings' | 'village' | 'collection';
/**
 * The screens reached from the main menu, outside the row-driven routing (ui/RouteDriver.tsx):
 * nothing pulls the player off them, not a World still gathering nor a party setting out. The
 * collection log (docs/131) is one: it belongs to the account, not to the World the player is in.
 */
export const MENU_SCREENS: readonly Screen[] = ['title', 'online', 'collection'];
export const offTheRoute = (screen: Screen): boolean => MENU_SCREENS.includes(screen);

export type Overlay = 'telling' | 'location' | 'hero';

export interface RouteStore {
  screen: Screen;
  overlay?: Overlay;
  selectedLocation?: bigint; partyId?: bigint; instanceId?: bigint; encounterId?: bigint;
  /** docs/51: the village the band walked into. */
  villageId?: bigint;
  /** The intro was opened from the Saga (a replay): the story rules leave it alone. */
  replay?: boolean;
  /** docs/86: the storybook chapter whose scene the chapter screen plays (a replay from the Saga). */
  chapter?: number;
  /** `before` runs as the screen changes (under the black, when the move fades); `cut` skips the fade. */
  go(screen: Screen, params?: Partial<Omit<RouteStore, 'go'>>, opts?: GoOptions): void;
}

export interface GoOptions { before?: () => void; cut?: boolean }

export const useRoute = create<RouteStore>()((set, get) => ({
  screen: 'title',
  go(screen, params = {}, opts = {}) {
    const from = get().screen;
    const swap = () => { opts.before?.(); set({ screen, ...params }); };
    const kind = opts.cut ? undefined : curtainFor(from, screen);
    if (kind) { cover(kind, screen, swap); return; }
    // a plain move to a third screen while another waits under the black wins
    const waiting = curtainPending();
    if (waiting !== undefined && screen !== from && screen !== waiting) uncover();
    swap();
  },
}));
